import { Alert, Grid } from '@mui/material'
import VerticalButtonBox from 'components/buttons/VerticalButtonBox'
import AgreeTermsCheckbox from 'components/form-elements/AgreeTermsCheckbox'
import FormInput from 'components/form-elements/FormInput'
import FormSelect from 'components/form-elements/FormSelect'
import FormPasswordCheckList from 'components/FormPasswordCheckList'
import { careLevels } from 'models/user.model'
import React, { useCallback } from 'react'
import { FieldValues, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { routes } from 'routes/routes'
import { AuthFormTitle } from '../AuthFormTitle/AuthFormTitle'
import {
  familyFormSettings,
  FamilyFormType,
  InviteFamilyFormType,
  inviteFamilyFormSettings,
} from './types'

interface Props {
  handleCreate: (form: FamilyFormType | InviteFamilyFormType) => void
  isInvite?: boolean
  inviteEmail?: string
  /** Care-team invite that also reserves a Family seat — registration completes without payment. */
  isFamilyInvite?: boolean
  /** Invite sent via SMS: the JWT has no email, so the user enters it here. */
  isPhoneOnlyInvite?: boolean
  /** Spinner shown on the Create button while the family-invite registration request is in flight. */
  submitLoading?: boolean
}

const SignUpFamilyForm = (props: Props) => {
  const { handleCreate, isInvite, inviteEmail, isFamilyInvite, isPhoneOnlyInvite, submitLoading } =
    props

  const methods = useForm(
    isInvite ? inviteFamilyFormSettings(inviteEmail || '') : familyFormSettings,
  )
  const { handleSubmit } = methods

  const onSubmitHandler: SubmitHandler<FieldValues> = useCallback(
    (values) => {
      handleCreate(values as FamilyFormType | InviteFamilyFormType)
    },
    [handleCreate],
  )

  const navigate = useNavigate()

  const onCancel = () => navigate(routes.signUp)

  return (
    <FormProvider {...methods}>
      <AuthFormTitle>Create Family Account</AuthFormTitle>

      {isFamilyInvite && (
        <Grid container sx={{ mb: 1.5 }}>
          <Grid item xs={12}>
            <Alert severity="info">
              You are invited to join a Family Subscription. No payment is required to complete your
              registration.
            </Alert>
          </Grid>
        </Grid>
      )}

      {isPhoneOnlyInvite && (
        <Grid container sx={{ mb: 1.5 }}>
          <Grid item xs={12}>
            <Alert severity="info">
              You were invited via text message. Please enter your email address — it will be used
              to sign in to your account.
            </Alert>
          </Grid>
        </Grid>
      )}

      <Grid
        container
        spacing={1.5}
        component="form"
        onSubmit={handleSubmit(onSubmitHandler)}
        noValidate
        autoComplete="off"
      >
        <Grid item xs={12} sm={6}>
          <FormInput label="First Name" name="firstName" required fullWidth />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormInput label="Last Name" name="lastName" required fullWidth />
        </Grid>

        <Grid item xs={12}>
          <FormInput
            label="My Email"
            name="email"
            required
            fullWidth
            autoComplete="off"
            disabled={isInvite && !!inviteEmail}
          />
        </Grid>

        {!isInvite && (
          <>
            <Grid item xs={12} sm={12} md={6}>
              <FormInput
                label="Person of Concern First Name"
                name="personOfConcernFirstName"
                required
                fullWidth
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <FormInput
                label="Person of Concern Last Name or Initial"
                name="personOfConcernLastNameOrInitial"
                required
                fullWidth
                autoComplete="off"
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info">
                Please note that the system will automatically create a new team for the entered
                Person of Concern. If you want to join the existing team please contact ProActive
                Memory Services team.
              </Alert>
            </Grid>
          </>
        )}

        <Grid item xs={12} sm={12}>
          <FormInput
            label="My new password"
            required
            fullWidth
            name="myNewPassword"
            type="password"
            autoComplete="new-password"
          />
          <FormPasswordCheckList name="myNewPassword" />
        </Grid>
        <Grid item xs={12} sm={12}>
          <FormInput
            label="Same new password"
            required
            fullWidth
            name="sameNewPassword"
            type="password"
            autoComplete="new-password"
          />
        </Grid>

        {!isInvite && (
          <Grid item xs={12}>
            <FormSelect
              label="What level of care does your person of concern need?"
              name="levelOfCareNeeded"
              required
              options={careLevels}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <AgreeTermsCheckbox name="readTermsAndConditions" />
        </Grid>

        <Grid item xs={12}>
          <VerticalButtonBox
            mt={1}
            onCancel={onCancel}
            buttonSaveType="submit"
            saveText="Create my Account"
            loading={submitLoading}
          />
        </Grid>
      </Grid>
    </FormProvider>
  )
}

export default SignUpFamilyForm
